/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { ButtonIcon } from "./..";
import PropTypes from 'prop-types';
import React, { forwardRef, memo, useContext, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { boxBorder, displayFlex, displayNone, flexCol, flexRow, justifyCenter, mg, overflowHidden, parseWidth, parseWidthHeight, pd, positionRelative, selfCenter, whiteSpaceNoWrap } from "../../styles/general";
import { classNames } from "../../utils";
import TabContext from "./context";
const TabHeader = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  centered,
  children,
  className,
  level,
  scrollButtons,
  style,
  width,
  ...props
}, reference) => {
  var _ref$current, _ref$current2;
  const {
    direction
  } = useContext(TabContext);
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const isVertical = direction === 'vertical';
  let showScrollLeftButton = !isVertical && scrollLeft > 0;
  let showScrollRightButton = !isVertical && (ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.offsetWidth) + scrollLeft < (ref === null || ref === void 0 ? void 0 : (_ref$current2 = ref.current) === null || _ref$current2 === void 0 ? void 0 : _ref$current2.scrollWidth);
  const _ContainerCSS = ContainerCSS(centered, isVertical, width);
  const _TabHeaderCSS = TabHeaderCSS(width);
  const ScrollLeftButtonCSS = ScrollButtonCSS(true, showScrollLeftButton, showScrollRightButton);
  const ScrollRightButtonCSS = ScrollButtonCSS(false, showScrollLeftButton, showScrollRightButton);
  const _onClickScrollButton = scrollPrevious => {
    let scrollValue = scrollPrevious ? ref.current.scrollLeft - (ref.current.offsetWidth + 30) : ref.current.scrollLeft + (ref.current.offsetWidth + 30);
    ref.current.scrollLeft = scrollValue;
    setScrollLeft(scrollValue);
  };
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    return jsx("div", {
      css: _ContainerCSS,
      className: classNames('DGN-UI-Tab-Header-Container', className),
      style: style,
      ...props
    }, scrollButtons && !isVertical ? jsx(ButtonIcon, {
      css: ScrollLeftButtonCSS,
      name: isVertical ? 'ArrowUp' : 'ArrowLeft',
      viewType: 'text',
      size: 'tiny',
      viewBox: true,
      disabled: !showScrollLeftButton,
      onClick: () => _onClickScrollButton(true)
    }) : null, jsx("div", {
      css: [_TabHeaderCSS, !isVertical && TabHeaderHorizontalCSS],
      className: 'DGN-UI-Tab-Header',
      ref: ref,
      onScroll: e => {
        var _e$target;
        return scrollButtons && setScrollLeft((e === null || e === void 0 ? void 0 : (_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.scrollLeft) || 0);
      }
    }, React.Children.map(children, child => child && /*#__PURE__*/React.cloneElement(child, {
      level
    }))), scrollButtons && !isVertical ? jsx(ButtonIcon, {
      css: ScrollRightButtonCSS,
      name: isVertical ? 'ArrowDown' : 'ArrowRight',
      viewType: 'text',
      size: 'tiny',
      viewBox: true,
      disabled: !showScrollRightButton,
      onClick: () => _onClickScrollButton(false)
    }) : null);
  }, [centered, children, className, level, props, scrollButtons, style, width, direction, isVertical, scrollLeft]);
}));
const ContainerCSS = (centered, isVertical, width) => css`
    ${displayFlex};
    ${overflowHidden};
    ${positionRelative};
    ${boxBorder};
    ${parseWidthHeight(width, 'auto')};
    ${centered && justifyCenter};
    ${isVertical ? flexCol : flexRow};
    ${pd(0)};
`;
const TabHeaderCSS = width => css`
    ${displayFlex};
    ${flexCol};
    ${parseWidth(width)};
`;
const TabHeaderHorizontalCSS = css`
    ${displayFlex};
    ${flexRow};
    ${positionRelative};
    ${whiteSpaceNoWrap};
    ${mg(0)};
    overflow-x: auto;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;
    scroll-padding: 50%;
    transition: 0.3s ease-in-out 0.3s;
    &::-webkit-scrollbar {
        ${displayNone};
    }
`;
const ScrollButtonCSS = (scrollPrevious, showScrollLeftButton, showScrollRightButton) => css`
    ${parseWidthHeight(24, 24)};
    ${selfCenter};
    ${mg(scrollPrevious ? [0, 2, 0, 0] : [0, 0, 0, 2])}
    overflow: initial;
    opacity: ${scrollPrevious && showScrollLeftButton || !scrollPrevious && showScrollRightButton ? '1' : '0'};
`;
TabHeader.defaultProps = {
  centered: false,
  className: '',
  scrollButtons: false,
  style: {},
  width: '100%'
};
TabHeader.propTypes = {
  /** If true, the tabs are centered. */
  centered: PropTypes.bool,
  /** Element to display in Tab header. */
  children: PropTypes.array,
  /** Class for component. */
  className: PropTypes.string,
  /** If true, show scroll buttons. */
  scrollButtons: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Width of component. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Any props else. */
  props: PropTypes.any
};
export default TabHeader;